import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import { getAdminAbandonedCheckouts, recoverAbandonedCheckout } from '../../api/client';
import { ShoppingCart, RefreshCw, Mail, MessageSquare, Loader2, CheckCircle } from 'lucide-react';

interface AbandonedCart {
  id: number;
  user_name: string;
  email: string;
  phone: string;
  course_title: string;
  amount: number;
  utm_source: string;
  recovery_email_sent: number;
  recovered_at: string | null;
  created_at: string;
}

export default function AdminAbandonedCarts(): React.JSX.Element {
  const [carts, setCarts] = useState<AbandonedCart[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [recoveringId, setRecoveringId] = useState<number | null>(null);

  const fetchCarts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminAbandonedCheckouts({ page: String(page), per_page: '20' });
      const data = res.data?.data ?? [];
      const meta = {
        totalPages: res.data?.totalPages ?? 1,
      };
      setCarts(data);
      setTotalPages(meta.totalPages);
    } catch (err: any) {
      console.error('Failed to load abandoned carts:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchCarts();
  }, [fetchCarts]);

  const handleRecover = async (id: number) => {
    setRecoveringId(id);
    try {
      await recoverAbandonedCheckout(id);
      setCarts((prev) =>
        prev.map((c) => (c.id === id ? { ...c, recovery_email_sent: 1 } : c))
      );
    } catch (err: any) {
      alert(err.message || 'Failed to send recovery');
    } finally {
      setRecoveringId(null);
    }
  };

  const recoveredCount = carts.filter((c) => c.recovered_at).length;
  const recoveryRate = carts.length > 0 ? Math.round((recoveredCount / carts.length) * 100) : 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Abandoned Carts</h1>
            <p className="text-sm text-slate-500">Recover lost sales with automated WhatsApp & email</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Abandoned', value: carts.length, color: 'text-slate-900' },
            { label: 'Recovery Sent', value: carts.filter((c) => c.recovery_email_sent).length, color: 'text-amber-500' },
            { label: 'Recovered', value: recoveredCount, color: 'text-green-600' },
            { label: 'Recovery Rate', value: `${recoveryRate}%`, color: 'text-emerald-600' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 border border-slate-100">
              <p className="text-xs font-bold text-slate-500 uppercase">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color} mt-1`}>{stat.value}</p>
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">User</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">Course</th>
                  <th className="text-right text-xs font-bold text-slate-500 uppercase px-5 py-3">Amount</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">Source</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">Recovery</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                    </td>
                  </tr>
                ) : carts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-500">
                      No abandoned carts found
                    </td>
                  </tr>
                ) : (
                  carts.map((cart) => (
                    <tr key={cart.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{cart.user_name || 'Guest'}</p>
                          <p className="text-xs text-slate-500">{cart.email || cart.phone}</p>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{cart.course_title}</td>
                      <td className="px-5 py-4 text-right text-sm font-bold text-slate-900">
                        ₹{Number(cart.amount).toLocaleString()}
                      </td>
                      <td className="px-5 py-4">
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                          {cart.utm_source || 'direct'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {cart.recovered_at ? (
                          <span className="flex items-center gap-1 text-xs font-bold text-green-600">
                            <CheckCircle className="w-3.5 h-3.5" /> Recovered
                          </span>
                        ) : cart.recovery_email_sent ? (
                          <span className="flex items-center gap-1 text-xs font-bold text-amber-600">
                            <Mail className="w-3.5 h-3.5" /> Sent
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">Pending</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {!cart.recovery_email_sent && !cart.recovered_at && (
                          <button
                            onClick={() => handleRecover(cart.id)}
                            disabled={recoveringId === cart.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition-colors disabled:opacity-50"
                          >
                            {recoveringId === cart.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3" />
                            )}
                            Recover
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && carts.length > 0 && (
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100">
              <p className="text-xs text-slate-500">Page {page} of {totalPages}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AdminLayout>
  );
}
